import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../../../component/elements/CustomButton/CustomButton";
import TextFieldCustom from "../../../../component/elements/textfieldCustom";
import { useSnackbar } from "../../../../component/SnackbarContext";
import axiosInstance from "../../../../configuration/request";
import { validateForm } from "../../../../utils/formValidation/validation";
import { missMatchRules } from "../../../../utils/formValidation/validationRules";
import { FiUploadCloud } from "react-icons/fi";
import ImageUploader from "../../../../component/imageUploader";

function MissMatchOrder({ onClose, dialogDetails, onSubmit }) {
  const [formData, setFormData] = useState({
    expectedValue: "",
    actualValue: "",
    additionalCharge: "",
    message: "",
    imageUrl: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [breakdown, setBreakdown] = useState({
    amount: 0,
    storeAmount: 0,
    tydiiAmount: 0,
  });
  const showSnackbar = useSnackbar();

  // Updated Calculation: Input is Mismatch Amount, Fee is added on top
  useEffect(() => {
    const mismatchAmount = Number(formData.additionalCharge) || 0;
    const tydiiPercentage = 0.25; // 25%
    
    // Calculate Fee and Total
    const tydiiFee = mismatchAmount * tydiiPercentage;
    const totalPayable = mismatchAmount ;

    setBreakdown({
      amount: parseFloat(totalPayable.toFixed(2)),      // Net Payable (Base + Fee)
      storeAmount: parseFloat(mismatchAmount.toFixed(2)) - parseFloat(tydiiFee.toFixed(2)), // Base Mismatch Amount
      tydiiAmount: parseFloat(tydiiFee.toFixed(2)),       // Tydii Fee
    });
  }, [formData.additionalCharge]);

  const handleChange = (field) => (e) => {
    const value = e.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const updateMissmatch = () => {
    const errors = validateForm(formData, missMatchRules);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      type:
        dialogDetails.service_type === "Wash & Fold" ? "WEIGHT" : "QUANTITY",
      expectedValue: Number(formData.expectedValue),
      actualValue: Number(formData.actualValue),
      // IMPORTANT: Send the Total Payable amount as the main charge to the API
      additionalCharge: breakdown.amount, 
      breakdown: {
        amount: breakdown.amount,
        storeAmount: breakdown.storeAmount,
        tydiiAmount: breakdown.tydiiAmount,
      },
      message: formData.message,
      imageUrl: formData.imageUrl,
    };

    axiosInstance
      .post(`/customer/chat/mismtch/${dialogDetails?.order_id}`, payload)
      .then(() => {
        onSubmit();
      })
      .catch((error) => {
        showSnackbar(
          error?.response?.data?.message || "Something went wrong!",
          "error"
        );
      });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold text-gray-800">
            Report Order Mismatch
          </span>
        </div>
        <button onClick={onClose} aria-label="Close">
          <CloseIcon className="text-gray-500 hover:text-gray-700" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-0 gap-x-3">
        <TextFieldCustom
          label="Expected quantity/weight*"
          fullWidth
          value={formData.expectedValue}
          onChange={handleChange("expectedValue")}
          error={!!formErrors.expectedValue}
          helperText={formErrors.expectedValue || " "}
        />
        <TextFieldCustom
          label="Actual quantity/weight*"
          fullWidth
          value={formData.actualValue}
          onChange={handleChange("actualValue")}
          error={!!formErrors.actualValue}
          helperText={formErrors.actualValue || " "}
        />
        <TextFieldCustom
          label="Mismatch Amount (Store Charge)*" 
          fullWidth
          value={formData.additionalCharge}
          onChange={handleChange("additionalCharge")}
          error={!!formErrors.additionalCharge}
          helperText={formErrors.additionalCharge || " "}
        />
      </div>

      {/* Breakdown Section */}
      {formData.additionalCharge && Number(formData.additionalCharge) > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Payment Breakdown
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Mismatch Amount :</span>
              <span className="font-medium text-gray-800">
                ${breakdown.storeAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Tydii Processing Fee :</span>
              <span className="font-medium text-gray-800">
                + ${breakdown.tydiiAmount.toFixed(2)}
              </span>
            </div>
            <div className="border-t border-blue-300 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">
                  Total Net Payable :
                </span>
                <span className="text-lg font-bold text-blue-600">
                  ${breakdown.amount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <TextFieldCustom
        label="Message*"
        fullWidth
        value={formData.message}
        onChange={handleChange("message")}
        error={!!formErrors.message}
        helperText={formErrors.message || " "}
        heightClass={"custom-textarea-height"}
        multiline
        rows={2}
      />

      <ImageUploader
        value={formData.imageUrl}
        onChange={(fileUrl) =>
          setFormData((prev) => ({ ...prev, imageUrl: fileUrl }))
        }
      />

      {/* Footer */}
      <div className="flex justify-center mt-6 gap-3">
        <CustomButton
          onClick={onClose}
          customeClass="primary-custom-button w-[150px] h-[40px]"
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={updateMissmatch}
          customeClass="gradient-button w-full sm:w-[150px] h-[40px]"
        >
          Submit
        </CustomButton>
      </div>
    </div>
  );
}

export default MissMatchOrder;
